import { useMemo, useState } from "react";
import {
  Award,
  BookOpenCheck,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  Plus,
  Trash2,
  UserCheck,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import {
  TRAINING_LIBRARY,
  type TrainingModuleDefinition,
} from "../data/trainingLibrary";
import type { CompetenceRecord, TrainingAssignment } from "../types/domain";

interface CompetenceProps {
  records: CompetenceRecord[];
  assignments: TrainingAssignment[];
  onSaveRecord: (record: CompetenceRecord) => void;
  onRemoveRecord: (id: string) => void;
  onSaveAssignment: (assignment: TrainingAssignment) => void;
}

const blankRecord = (): CompetenceRecord => ({
  id: crypto.randomUUID(),
  personName: "",
  organisation: "",
  role: "",
  competenceAreas: [],
  evidenceReference: "",
  assessor: "",
  assessedDate: "",
  expiryDate: "",
  status: "not-assessed",
  notes: "",
});

export function CompetenceView({
  records,
  assignments,
  onSaveRecord,
  onRemoveRecord,
  onSaveAssignment,
}: CompetenceProps) {
  const [draft, setDraft] = useState<CompetenceRecord | null>(null);
  const [person, setPerson] = useState("");
  const [learning, setLearning] = useState<{
    module: TrainingModuleDefinition;
    assignment: TrainingAssignment | null;
  } | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [quizResult, setQuizResult] = useState<number | null>(null);
  const completed = assignments.filter((item) => item.status === "completed").length;
  const expiring = useMemo(() => {
    const limit = new Date();
    limit.setDate(limit.getDate() + 60);
    const date = limit.toISOString().slice(0, 10);
    return records.filter((item) => item.expiryDate && item.expiryDate <= date).length;
  }, [records]);

  const assign = (moduleId: string) => {
    if (!person.trim()) {
      window.alert("Enter a person or role before assigning training.");
      return;
    }
    const due = new Date();
    due.setDate(due.getDate() + 30);
    onSaveAssignment({
      id: crypto.randomUUID(),
      moduleId,
      personName: person.trim(),
      assignedDate: new Date().toISOString().slice(0, 10),
      dueDate: due.toISOString().slice(0, 10),
      completedDate: "",
      score: null,
      status: "assigned",
      evidenceId: "",
    });
  };

  const openLearning = (
    module: TrainingModuleDefinition,
    assignment: TrainingAssignment | null = null,
  ) => {
    setLearning({ module, assignment });
    setAnswers({});
    setQuizResult(null);
  };

  const completeKnowledgeCheck = () => {
    if (!learning) return;
    const correct = learning.module.knowledgeChecks.filter(
      (check, index) => answers[index] === check.answer,
    ).length;
    const score = Math.round((correct / learning.module.knowledgeChecks.length) * 100);
    setQuizResult(score);
    if (learning.assignment) {
      onSaveAssignment({
        ...learning.assignment,
        score,
        status: score >= 80 ? "completed" : "in-progress",
        completedDate: score >= 80 ? new Date().toISOString().slice(0, 10) : "",
      });
    }
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker"><UserCheck size={16} /> Competence assurance</span>
          <h2>Prove capability, then close the knowledge gap.</h2>
          <p>
            Regulation 10 requires skills, knowledge, experience and organisational capability.
            Record the assessment evidence and assign contextual learning tied to the user’s real
            project hazards.
          </p>
        </div>
        <div className="mini-metrics">
          <span><strong>{records.length}</strong> assessed</span>
          <span><strong>{completed}</strong> modules complete</span>
          <span><strong>{expiring}</strong> expiring</span>
        </div>
      </section>

      <section className="section-heading-row">
        <div><span className="eyebrow">Capability register</span><h2>Project dutyholders</h2></div>
        <button className="button button-primary" onClick={() => setDraft(blankRecord())}>
          <Plus size={18} /> Assess competence
        </button>
      </section>

      {records.length === 0 ? (
        <EmptyState
          icon={<Award size={30} />}
          title="No competence assessments"
          action={<button className="button button-primary" onClick={() => setDraft(blankRecord())}><Plus size={18} /> Add assessment</button>}
        >
          Record role-specific capability, evidence, assessor and expiry. A course certificate
          alone is not treated as proof of competence.
        </EmptyState>
      ) : (
        <section className="competence-grid">
          {records.map((record) => (
            <article key={record.id}>
              <header>
                <div><span>{record.role || "Role not set"}</span><h3>{record.personName}</h3></div>
                <StatusPill status={record.status === "competent" ? "success" : record.status === "expired" ? "danger" : "warning"}>{record.status}</StatusPill>
              </header>
              <p>{record.organisation || "Organisation not set"}</p>
              <div className="tag-row">{record.competenceAreas.map((area) => <span key={area}>{area}</span>)}</div>
              <dl>
                <div><dt>Evidence</dt><dd>{record.evidenceReference || "Missing"}</dd></div>
                <div><dt>Assessor</dt><dd>{record.assessor || "Missing"}</dd></div>
                <div><dt>Expiry</dt><dd>{record.expiryDate || "Not set"}</dd></div>
              </dl>
              <footer>
                <button className="button-link" onClick={() => setDraft(record)}>Edit</button>
                <button className="icon-button danger" aria-label="Delete record" onClick={() => window.confirm("Delete this competence record?") && onRemoveRecord(record.id)}><Trash2 size={16} /></button>
              </footer>
            </article>
          ))}
        </section>
      )}

      <section className="training-section">
        <div className="section-heading-row">
          <div><span className="eyebrow">Embedded learning</span><h2>Contextual training library</h2></div>
          <label className="assign-person">
            <span>Assign to</span>
            <input value={person} onChange={(e) => setPerson(e.target.value)} placeholder="Person or project role" />
          </label>
        </div>
        <div className="training-grid">
          {TRAINING_LIBRARY.map((module) => {
            const moduleAssignments = assignments.filter((item) => item.moduleId === module.id);
            return (
              <article key={module.id}>
                <header><BookOpenCheck size={20} /><span>{module.durationMinutes} min</span></header>
                <h3>{module.title}</h3>
                <p>{module.outcome}</p>
                <div className="tag-row">{module.topics.slice(0, 3).map((topic) => <span key={topic}>{topic}</span>)}</div>
                <small>{moduleAssignments.length} assignment(s)</small>
                <footer>
                  <a href={module.sourceUrl} target="_blank" rel="noreferrer">Source <ExternalLink size={13} /></a>
                  <button className="button-link" onClick={() => openLearning(module)}>
                    Open module
                  </button>
                  <button className="button button-secondary" onClick={() => assign(module.id)}>Assign</button>
                </footer>
              </article>
            );
          })}
        </div>
      </section>

      <section className="training-section assignment-section">
        <div className="section-heading-row">
          <div>
            <span className="eyebrow">Completion evidence</span>
            <h2>Training assignments</h2>
          </div>
          <StatusPill status={assignments.some((item) => item.status !== "completed") ? "warning" : "success"}>
            {assignments.length} assignment(s)
          </StatusPill>
        </div>
        {assignments.length === 0 ? (
          <p className="table-empty">No learning has been assigned yet.</p>
        ) : (
          <div className="assignment-list">
            {assignments.map((assignment) => {
              const module = TRAINING_LIBRARY.find((item) => item.id === assignment.moduleId);
              if (!module) return null;
              const overdue =
                assignment.status !== "completed" &&
                Boolean(assignment.dueDate && assignment.dueDate < new Date().toISOString().slice(0, 10));
              return (
                <article key={assignment.id}>
                  <span className="assignment-icon"><ClipboardCheck size={18} /></span>
                  <div>
                    <strong>{assignment.personName}</strong>
                    <small>{module.title} · due {assignment.dueDate || "not set"}</small>
                  </div>
                  <StatusPill status={assignment.status === "completed" ? "success" : overdue ? "danger" : "warning"}>
                    {assignment.status}{assignment.score !== null ? ` · ${assignment.score}%` : ""}
                  </StatusPill>
                  <button className="button button-secondary" onClick={() => openLearning(module, assignment)}>
                    {assignment.status === "completed" ? "Retake" : "Complete"}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {learning && (
        <div className="modal-backdrop">
          <section className="modal-card training-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">{learning.module.durationMinutes} minute learning module</span>
                <h2>{learning.module.title}</h2>
              </div>
              <button className="icon-button" onClick={() => setLearning(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <p className="training-outcome">{learning.module.outcome}</p>
              <div className="lesson-list">
                {learning.module.lessons.map((lesson, index) => (
                  <article key={lesson.title}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <h3>{lesson.title}</h3>
                      <p>{lesson.content}</p>
                      <div className="practice-prompt">
                        <strong>Apply it in this project</strong>
                        <p>{lesson.practice}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <div className="knowledge-check">
                <span className="eyebrow">Knowledge check · 80% pass mark</span>
                {learning.module.knowledgeChecks.map((check, questionIndex) => (
                  <fieldset key={check.question}>
                    <legend>{questionIndex + 1}. {check.question}</legend>
                    {check.options.map((option, optionIndex) => (
                      <label key={option}>
                        <input
                          type="radio"
                          name={`question-${questionIndex}`}
                          checked={answers[questionIndex] === optionIndex}
                          onChange={() =>
                            setAnswers((current) => ({
                              ...current,
                              [questionIndex]: optionIndex,
                            }))
                          }
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                    {quizResult !== null && (
                      <p className={answers[questionIndex] === check.answer ? "answer-correct" : "answer-review"}>
                        {check.explanation}
                      </p>
                    )}
                  </fieldset>
                ))}
                {quizResult !== null && (
                  <div className={quizResult >= 80 ? "quiz-result quiz-pass" : "quiz-result quiz-retry"}>
                    <CheckCircle2 size={20} />
                    <strong>{quizResult}% · {quizResult >= 80 ? "Completed" : "Review and retry"}</strong>
                  </div>
                )}
              </div>
              <a className="source-link" href={learning.module.sourceUrl} target="_blank" rel="noreferrer">
                {learning.module.sourceLabel} <ExternalLink size={14} />
              </a>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setLearning(null)}>Close</button>
              <button
                className="button button-primary"
                disabled={Object.keys(answers).length !== learning.module.knowledgeChecks.length}
                onClick={completeKnowledgeCheck}
              >
                Score knowledge check
              </button>
            </div>
          </section>
        </div>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card modal-compact" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div><span className="eyebrow">Regulation 10 capability</span><h2>Competence assessment</h2></div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Person">
                  <input value={draft.personName} onChange={(e) => setDraft({ ...draft, personName: e.target.value })} />
                </Field>
                <Field label="Organisation">
                  <input value={draft.organisation} onChange={(e) => setDraft({ ...draft, organisation: e.target.value })} />
                </Field>
                <Field label="Project role">
                  <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
                </Field>
                <Field label="Assessment status">
                  <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as CompetenceRecord["status"] })}>
                    <option value="not-assessed">Not assessed</option>
                    <option value="development-required">Development required</option>
                    <option value="competent">Competent</option>
                    <option value="expired">Expired</option>
                  </select>
                </Field>
                <Field label="Competence areas" hint="Comma separated" wide>
                  <input value={draft.competenceAreas.join(", ")} onChange={(e) => setDraft({ ...draft, competenceAreas: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })} />
                </Field>
                <Field label="Evidence reference" wide>
                  <input value={draft.evidenceReference} onChange={(e) => setDraft({ ...draft, evidenceReference: e.target.value })} />
                </Field>
                <Field label="Assessor">
                  <input value={draft.assessor} onChange={(e) => setDraft({ ...draft, assessor: e.target.value })} />
                </Field>
                <Field label="Assessed date">
                  <input type="date" value={draft.assessedDate} onChange={(e) => setDraft({ ...draft, assessedDate: e.target.value })} />
                </Field>
                <Field label="Expiry / review date">
                  <input type="date" value={draft.expiryDate} onChange={(e) => setDraft({ ...draft, expiryDate: e.target.value })} />
                </Field>
                <Field label="Limitations and development actions" wide>
                  <textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.personName && draft.role && draft.assessor ? <><CheckCircle2 size={17} /> Assessment accountable</> : <><Award size={17} /> Person, role and assessor required</>}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!draft.personName.trim() || !draft.role.trim() || !draft.assessor.trim()}
                onClick={() => {
                  onSaveRecord(draft);
                  setDraft(null);
                }}
              >
                Save assessment
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
